import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, Users, CheckCircle2, Loader2, X, Sparkles, Clock } from 'lucide-react';
import { SectionHeader, EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { BACKEND_URL } from '@/config/api';
import { fmtMoney, formatLocale } from '@/lib/format';
import { useSiteLang } from '@/components/site/SiteLang';
import { useDashboard } from '@/context/DashboardContext';
import { BillingChannelPicker, LockedChannelNote, needsBillingChoice, lockedChannel } from '@/components/dashboard/BillingChannelPicker';

const day = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`);
const fmtRange = (a, b) => {
  const o = { day: 'numeric', month: 'long' };
  const A = day(a); const B = day(b || a);
  return A.toDateString() === B.toDateString()
    ? A.toLocaleDateString(formatLocale(), { ...o, year: 'numeric' })
    : `${A.toLocaleDateString(formatLocale(), o)} – ${B.toLocaleDateString(formatLocale(), { ...o, year: 'numeric' })}`;
};
const fmtEventDate = (iso) => day(iso).toLocaleDateString(formatLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
const flyer = (u) => (u ? `${BACKEND_URL}${u}` : null);

/*
  Inglés (05/11/2026): títulos, descripciones y lugares de eventos y bootcamps vienen de la BD en español y se
  muestran tal cual, con el aviso «Content delivered in Spanish» una vez por pantalla.
*/
const T = {
  es: {
    regLabel: { approved: 'Plaza confirmada', pending: 'Solicitud en revisión', awaiting_payment: 'Pago pendiente' },
    registered: 'Inscrito',
    upsellTitle: '¿Prefieres hacerte Premium?',
    until: (d) => ` (hasta el ${d})`,
    upNull: (from, amt, u) => `Pasa de ${from} a Premium por ${amt}${u}, con el bootcamp incluido.`,
    upLess: (from, amt, u) => `Pasa de ${from} a Premium por ${amt}${u}: menos que la plaza suelta, y el bootcamp queda incluido.`,
    upSame: (from, amt, u, exact) => `Pasa de ${from} a Premium por ${amt}${u}, ${exact ? 'lo mismo' : 'prácticamente lo mismo'} que la plaza suelta: el bootcamp queda incluido y además eres Premium.`,
    upMore: (from, amt, u, more) => `Pasa de ${from} a Premium por ${amt}${u}${more ? `: solo ${more} más que la plaza suelta` : ''}, con el bootcamp incluido.`,
    newPremium: (amt, more) => `La membresía Premium incluye tu plaza en el bootcamp y un año de Premium por ${amt}${more ? `: solo ${more} más que la plaza suelta.` : '.'}`,
    keepsLifetime: 'Mantienes tu condición vitalicia.',
    goPremium: 'Hazte Premium',
    perYear: '/ año',
    upgradeTitle: (from) => `Pasar de ${from} a Premium`,
    premiumTitle: 'Membresía Premium',
    howPay: '¿Cómo quieres pagar?',
    booked: '¡Plaza reservada! Te enviaremos los detalles logísticos por email.',
    buyPlace: 'Comprar plaza',
    bookPlace: 'Reservar mi plaza',
    close: 'Cerrar',
    orJustPlace: 'O compra solo la plaza',
    diet: 'Alergias o intolerancias',
    optional: 'Opcional',
    expect: '¿Qué esperas del bootcamp?',
    logistics: 'Alojamiento y dietas no incluidos. Recibirás los detalles logísticos por email.',
    pay: (amt) => `Pagar ${amt}`,
    confirm: 'Confirmar reserva',
    placeTitle: (title) => `Plaza ${title || 'Bootcamp Broker Digital'}`,
    paid: 'Pago recibido. Tu plaza se confirma en unos segundos.',
    cancelled: 'Pago cancelado. Tu plaza no se ha reservado.',
    joinedCocktail: 'Apuntado. El equipo te enviará cómo pagar el cóctel.',
    joined: '¡Apuntado! Te esperamos.',
    title: 'Eventos y bootcamp',
    lockedSubtitle: 'Encuentros del club y Bootcamp Broker Digital',
    lockedTitle: 'Los eventos son para socios',
    lockedText: 'Activa tu membresía para ver el calendario del club y reservar tu plaza en los encuentros y en el Bootcamp.',
    viewMemberships: 'Ver membresías',
    subtitle: 'Reserva tu plaza en los encuentros del club y en el Bootcamp Broker Digital',
    nothingTitle: 'No hay fechas abiertas ahora mismo',
    nothingText: 'En cuanto publiquemos el próximo encuentro o edición del Bootcamp aparecerá aquí.',
    bootcamp: 'Bootcamp Broker Digital',
    full: 'Completo',
    spots: (n) => (n === 1 ? '1 plaza libre' : `${n} plazas libres`),
    included: 'Incluido en tu Premium',
    noSpots: 'No quedan plazas en esta edición.',
    buyPlaceAmt: (amt) => `Comprar plaza · ${amt}`,
    orPremium: 'o hazte Premium y la tienes incluida',
    unfinished: 'Tienes un pago sin completar',
    eventsLocked: 'Los encuentros del club son para socios.',
    meetups: 'Encuentros del club',
    time: (h) => `${h} h`,
    signedUp: 'Estás apuntado',
    join: 'Apuntarme',
    cocktail: 'Con cóctel',
  },
  en: {
    regLabel: { approved: 'Place confirmed', pending: 'Request under review', awaiting_payment: 'Payment pending' },
    registered: 'Registered',
    upsellTitle: 'Would you rather go Premium?',
    until: (d) => ` (until ${d})`,
    upNull: (from, amt, u) => `Upgrade from ${from} to Premium for ${amt}${u}, with the bootcamp included.`,
    upLess: (from, amt, u) => `Upgrade from ${from} to Premium for ${amt}${u}: less than a standalone place, and the bootcamp is included.`,
    upSame: (from, amt, u, exact) => `Upgrade from ${from} to Premium for ${amt}${u}, ${exact ? 'the same as' : 'virtually the same as'} a standalone place: the bootcamp is included and you’re Premium too.`,
    upMore: (from, amt, u, more) => `Upgrade from ${from} to Premium for ${amt}${u}${more ? `: just ${more} more than a standalone place` : ''}, with the bootcamp included.`,
    newPremium: (amt, more) => `Premium membership includes your bootcamp place and a year of Premium for ${amt}${more ? `: just ${more} more than a standalone place.` : '.'}`,
    keepsLifetime: 'You keep your lifetime membership.',
    goPremium: 'Go Premium',
    perYear: '/ year',
    upgradeTitle: (from) => `Upgrade from ${from} to Premium`,
    premiumTitle: 'Premium membership',
    howPay: 'How would you like to pay?',
    booked: 'Place booked! We’ll email you the practical details.',
    buyPlace: 'Buy a place',
    bookPlace: 'Book my place',
    close: 'Close',
    orJustPlace: 'Or just buy the place',
    diet: 'Allergies or intolerances',
    optional: 'Optional',
    expect: 'What are you hoping to get from the bootcamp?',
    logistics: 'Accommodation and meals not included. We’ll email you the practical details.',
    pay: (amt) => `Pay ${amt}`,
    confirm: 'Confirm booking',
    placeTitle: (title) => `Place at ${title || 'Digital Broker Bootcamp'}`,
    paid: 'Payment received. Your place will be confirmed in a few seconds.',
    cancelled: 'Payment cancelled. Your place hasn’t been booked.',
    joinedCocktail: 'You’re signed up. The team will tell you how to pay for the cocktail.',
    joined: 'You’re signed up! See you there.',
    title: 'Events & bootcamp',
    lockedSubtitle: 'Club meet-ups and the Digital Broker Bootcamp',
    lockedTitle: 'Events are for members',
    lockedText: 'Activate your membership to see the club calendar and book your place at meet-ups and the Bootcamp.',
    viewMemberships: 'View memberships',
    subtitle: 'Book your place at club meet-ups and the Digital Broker Bootcamp',
    nothingTitle: 'No dates open right now',
    nothingText: 'As soon as we publish the next meet-up or Bootcamp edition, it’ll appear here.',
    bootcamp: 'Digital Broker Bootcamp',
    full: 'Full',
    spots: (n) => (n === 1 ? '1 place left' : `${n} places left`),
    included: 'Included in your Premium',
    noSpots: 'There are no places left for this edition.',
    buyPlaceAmt: (amt) => `Buy a place · ${amt}`,
    orPremium: 'or go Premium and it’s included',
    unfinished: 'You have an unfinished payment',
    eventsLocked: 'Club meet-ups are for members.',
    meetups: 'Club meet-ups',
    time: (h) => h,
    signedUp: 'You’re signed up',
    join: 'Sign me up',
    cocktail: 'With cocktail',
  },
};

// ¿Prefieres hacerte Premium? — la membresía Premium incluye el bootcamp
function PremiumUpsell({ offer, price, currency, billing, compact = false, t }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);
  if (!offer) return null;
  const extra = offer.currency === currency ? offer.amount - price : null;
  const until = offer.early && offer.deadline ? new Date(offer.deadline).toLocaleDateString(formatLocale(), { day: 'numeric', month: 'long' }) : null;
  // Upgrade de quien ya tiene una anual: misma vía de pago que su suscripción
  const locked = offer.upgrade ? lockedChannel(billing) : null;
  const pay = async (channel) => {
    setBusy(true);
    setBusyChannel(channel);
    try {
      const { checkout_url: url } = await dashboardApi.createCheckout('premium', channel);
      window.location.href = url;
    } catch (e) { toast.error(e.message); setBusy(false); setBusyChannel(null); }
  };
  const amt = fmtMoney(offer.amount, offer.currency);
  const u = until ? t.until(until) : '';
  const go = () => {
    if (needsBillingChoice(billing, offer.currency, !!locked)) setPicking(true);
    else pay(locked || 'particular');
  };
  return (
    <div className={`rounded-xl border border-[#c9a84c]/30 bg-[#c9a84c]/[0.07] ${compact ? 'p-4' : 'p-5'} space-y-2`} data-testid="bootcamp-upsell">
      <p className="text-white font-medium inline-flex items-center gap-2"><Sparkles size={15} className="text-[#c9a84c]" /> {t.upsellTitle}</p>
      <p className="text-sm text-gray-300">
        {offer.upgrade
          ? (extra == null
            ? t.upNull(offer.from_name, amt, u)
            : extra < -1
            ? t.upLess(offer.from_name, amt, u)
            : Math.abs(extra) <= 1
              ? t.upSame(offer.from_name, amt, u, extra === 0)
              : t.upMore(offer.from_name, amt, u, extra > 0 ? fmtMoney(extra, currency) : null))
          : t.newPremium(amt, extra > 0 ? fmtMoney(extra, currency) : null)}
      </p>
      {offer.keeps_lifetime && <p className="text-xs text-gray-400">{t.keepsLifetime}</p>}
      {locked && <LockedChannelNote billing={billing} amount={offer.amount} currency={offer.currency} />}
      <button type="button" onClick={go} disabled={busy} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50" data-testid="bootcamp-upsell-cta">
        {busy && <Loader2 size={14} className="animate-spin" />} {t.goPremium} · {amt}
      </button>
      <BillingChannelPicker open={picking} billing={billing} amount={offer.amount} currency={offer.currency}
        period={offer.upgrade ? null : t.perYear} title={offer.upgrade ? t.upgradeTitle(offer.from_name) : t.premiumTitle}
        subtitle={t.howPay} busyChannel={busyChannel} onSelect={pay}
        onClose={() => { if (!busyChannel) setPicking(false); }} />
    </div>
  );
}

function BootcampForm({ bootcamp, offer, billing, onClose, onDone, t }) {
  const [form, setForm] = useState({ dietary_restrictions: '', motivation: '', comments: '' });
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);
  const purchase = bootcamp.access === 'purchase';
  // Compra de la plaza: vía de pago (particular / empresa con IVA) y a Stripe
  const buy = async (channel) => {
    setBusy(true);
    setBusyChannel(channel);
    try {
      const { url } = await dashboardApi.checkoutBootcamp(bootcamp.id, form, channel);
      window.location.href = url;
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
      setBusyChannel(null);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (purchase) {
      if (needsBillingChoice(billing, bootcamp.currency)) setPicking(true);
      else buy('particular');
      return;
    }
    setBusy(true);
    try {
      await dashboardApi.reserveBootcamp(bootcamp.id, form);
      toast.success(t.booked);
      onDone();
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };
  const field = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c9a84c]/40';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="bc-form-title">
      <motion.form onSubmit={submit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0d1829] p-6 space-y-4" data-testid="bootcamp-form">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 id="bc-form-title" className="text-white font-semibold">{purchase ? t.buyPlace : t.bookPlace}</h3>
            <p className="text-sm text-gray-400">{bootcamp.title} · {fmtRange(bootcamp.start_date, bootcamp.end_date)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t.close}><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        {purchase && <PremiumUpsell offer={offer} price={bootcamp.price} currency={bootcamp.currency} billing={billing} compact t={t} />}
        {purchase && offer && <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold pt-1">{t.orJustPlace}</p>}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block" htmlFor="bc-diet">{t.diet}</label>
          <input id="bc-diet" value={form.dietary_restrictions} onChange={(e) => setForm({ ...form, dietary_restrictions: e.target.value })} className={field} placeholder={t.optional} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1 block" htmlFor="bc-mot">{t.expect}</label>
          <textarea id="bc-mot" rows={3} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} className={`${field} resize-none`} placeholder={t.optional} />
        </div>
        <p className="text-xs text-gray-400">{t.logistics}</p>
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#c9a84c] py-3 text-sm font-semibold text-[#0a1628] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="bootcamp-submit">
          {busy && <Loader2 size={15} className="animate-spin" />}
          {purchase ? t.pay(fmtMoney(bootcamp.price, bootcamp.currency)) : t.confirm}
        </button>
      </motion.form>
      {purchase && (
        <BillingChannelPicker open={picking} billing={billing} amount={bootcamp.price} currency={bootcamp.currency}
          title={t.placeTitle(bootcamp.title)} subtitle={t.howPay}
          busyChannel={busyChannel} onSelect={buy} onClose={() => { if (!busyChannel) setPicking(false); }} />
      )}
    </div>
  );
}

export default function DashboardEventos() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const { refreshMembership } = useDashboard();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [joining, setJoining] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    dashboardApi.memberEvents().then(setData).catch((e) => { toast.error(e.message); setData({ events: [], bootcamps: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const st = params.get('bootcamp');
    if (!st) return;
    if (st === 'success') { toast.success(t.paid); [2000, 6000].forEach((ms) => setTimeout(load, ms)); }
    if (st === 'cancel') toast.info(t.cancelled);
    setParams({}, { replace: true });
    refreshMembership?.();
  }, [params, setParams, load, refreshMembership, t]);

  const join = async (ev, ticket) => {
    setJoining(ev.slug);
    try {
      await dashboardApi.joinEvent(ev.slug, ticket);
      toast.success(ticket === 'cocktail' ? t.joinedCocktail : t.joined);
      load();
    } catch (e) { toast.error(e.message); } finally { setJoining(null); }
  };

  if (!data) return <PageSkeleton cards={0} rows={5} />;

  if (data.locked && !data.bootcamps?.length) {
    return (
      <div className="space-y-6" data-testid="dashboard-eventos">
        <SectionHeader title={t.title} subtitle={t.lockedSubtitle} />
        <EmptyState icon={CalendarDays} title={t.lockedTitle}
          text={t.lockedText}
          action={<Link to="/dashboard#membresias" className="inline-flex rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628]">{t.viewMemberships}</Link>} />
      </div>
    );
  }

  const nothing = !data.events.length && !data.bootcamps.length && !data.events_locked;
  return (
    <div className="space-y-8" data-testid="dashboard-eventos">
      <SectionHeader title={t.title} subtitle={t.subtitle} />
      {lang === 'en' && (data.events.length > 0 || data.bootcamps.length > 0) && <p className="-mt-6 text-[11px] text-gray-500" data-testid="content-lang-note">Content delivered in Spanish</p>}

      {nothing && <EmptyState icon={CalendarDays} title={t.nothingTitle} text={t.nothingText} />}

      {data.bootcamps.length > 0 && (
        <section aria-labelledby="bc-title" className="space-y-4">
          <h2 id="bc-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{t.bootcamp}</h2>
          {data.bootcamps.map((b) => {
            const reg = b.my_registration;
            const full = b.available_spots <= 0;
            return (
              <article key={b.id} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden md:flex" data-testid="bootcamp-card">
                {b.flyer_url && <img src={flyer(b.flyer_url)} alt="" className="md:w-56 h-44 md:h-auto object-cover" />}
                <div className="p-5 flex-1 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-white text-lg font-semibold">{b.title}</h3>
                      <p className="text-sm text-gray-400 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {fmtRange(b.start_date, b.end_date)}</span>
                        {(b.location || b.city) && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {[b.location, b.city].filter(Boolean).join(', ')}</span>}
                        <span className="inline-flex items-center gap-1.5"><Users size={14} /> {full ? t.full : t.spots(b.available_spots)}</span>
                      </p>
                    </div>
                    {b.access === 'included' && <span className="rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/10 px-3 py-1 text-xs text-[#c9a84c]">{t.included}</span>}
                  </div>
                  {b.description && <p className="text-sm text-gray-300 leading-relaxed">{b.description}</p>}
                  {reg && reg.status !== 'awaiting_payment' ? (
                    <p className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} /> {t.regLabel[reg.status] || t.registered}</p>
                  ) : full ? (
                    <p className="text-sm text-gray-400">{t.noSpots}</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <button onClick={() => setForm(b)} className="rounded-xl bg-[#c9a84c] px-5 py-2.5 text-sm font-semibold text-[#0a1628]" data-testid="bootcamp-cta">
                        {b.access === 'included' ? t.bookPlace : t.buyPlaceAmt(fmtMoney(b.price, b.currency))}
                      </button>
                      {b.access === 'purchase' && data.premium_offer && <span className="text-xs text-gray-400">{t.orPremium}</span>}
                      {reg?.status === 'awaiting_payment' && <span className="text-xs text-amber-200">{t.unfinished}</span>}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {data.events_locked && (
        <p className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-sm text-gray-400">
          {t.eventsLocked} <Link to="/dashboard#membresias" className="text-[#c9a84c] underline underline-offset-2">{t.viewMemberships}</Link>
        </p>
      )}

      {data.events.length > 0 && (
        <section aria-labelledby="ev-title" className="space-y-4">
          <h2 id="ev-title" className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{t.meetups}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {data.events.map((ev) => (
              <article key={ev.slug} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="event-card">
                {ev.flyer_url && <img src={flyer(ev.flyer_url)} alt="" className="w-full h-40 object-cover" />}
                <div className="p-5 space-y-3">
                  <h3 className="text-white font-semibold">{ev.title}</h3>
                  {ev.subtitle && <p className="text-sm text-gray-400">{ev.subtitle}</p>}
                  <p className="text-sm text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 capitalize"><CalendarDays size={14} /> {fmtEventDate(ev.event_date)}</span>
                    {ev.start_time && <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {t.time(ev.start_time)}</span>}
                    {(ev.location_name || ev.city) && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {[ev.location_name, ev.city].filter(Boolean).join(', ')}</span>}
                  </p>
                  {ev.my_registration ? (
                    <p className="inline-flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} /> {t.signedUp}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <button disabled={joining === ev.slug} onClick={() => join(ev, 'libre')} className="rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50">{t.join}</button>
                      {ev.cocktail_enabled && (
                        <button disabled={joining === ev.slug} onClick={() => join(ev, 'cocktail')} className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white disabled:opacity-50">
                          {t.cocktail}{ev.cocktail_price_eur ? ` · ${fmtMoney(ev.cocktail_price_eur, 'eur')}` : ''}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {form && <BootcampForm bootcamp={form} t={t} offer={data.premium_offer} billing={data.billing} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
    </div>
  );
}
